/**
 * PWA-01 «Баннер „Добавить на экран Домой“», PWA-02 «Инструкция установки»,
 * PWA-07 «Предложение установить приложение» (CLIENT_WEB.md §9.4, раздел 5).
 *
 * Ни один из экранов не выполняет сетевых запросов и не меняет серверное
 * состояние: всё решается уже загруженным `GET /me` и локальными счётчиками.
 */
import { useRouterState } from '@tanstack/react-router'

import { useInstallOffer } from '@features/pwa/use-install'
import { isStandalone } from '@shared/pwa/install'
import { Button, Icon, Sheet, cx } from '@shared/ui'
import { useEscape } from '@shared/lib/use-escape'
import { useIsDesktopLayout } from '@shared/lib/use-media-query'

import { useAppStore, selectIsPaused } from '../../model/app-store'
import styles from './install.module.css'

/** §9.4 п. 2: только корневые экраны разделов. */
const ROOT_ROUTES = new Set(['/dvizh', '/chats', '/contacts', '/settings'])
/** PWA-01 живёт ещё и над «Уведомлениями» и «Профилем» (раздел 5). */
const SAFARI_ROUTES = new Set([...ROOT_ROUTES, '/notifications', '/me'])

/* ===================================================================== */
/* Хост: решает, показывать ли и что именно                               */
/* ===================================================================== */

export function InstallAppHost(): React.ReactElement | null {
  const me = useAppStore((s) => s.me)
  const phase = useAppStore((s) => s.phase)
  const blocking = useAppStore((s) => s.blocking)
  const syncStatus = useAppStore((s) => s.syncStatus)
  const pushPromptOpen = useAppStore((s) => s.pushPromptOpen)
  const pushOfferOpen = useAppStore((s) => s.pushOfferOpen)
  const paused = useAppStore(selectIsPaused)
  const pathname = useRouterState({ select: (state) => state.location.pathname })
  const isDesktop = useIsDesktopLayout()

  const offer = useInstallOffer(me?.user.id ?? null)

  const routes = offer.variant === 'safari' ? SAFARI_ROUTES : ROOT_ROUTES

  /*
   * DoD PWA-07 п. 2 / PWA-01 п. 5: не в потоках входа и onboarding, не поверх
   * блокирующих состояний и модальных окон, не до завершения replay и не при
   * активной санкции. Полоса N-03 тоже имеет приоритет — два баннера подряд
   * перегружают верх экрана.
   */
  const contextAllows =
    phase === 'ready' &&
    !blocking &&
    !pushPromptOpen &&
    !pushOfferOpen &&
    !paused &&
    me?.user.status === 'active' &&
    me.onboarding.complete &&
    (me.sanctions ?? []).length === 0 &&
    syncStatus !== 'initial_snapshot' &&
    syncStatus !== 'replaying' &&
    syncStatus !== 'reset_required' &&
    routes.has(pathname) &&
    !isComposing()

  const visible = offer.allowed && contextAllows

  // §9.4 п. 2: `Esc` на desktop скрывает карточку так же, как «Позже».
  useEscape(visible && isDesktop, offer.dismiss)

  return (
    <>
      {visible && offer.variant === 'chromium' ? (
        <ChromiumBanner
          desktop={isDesktop}
          onInstall={() => void offer.install()}
          onDismiss={offer.dismiss}
        />
      ) : null}

      {visible && offer.variant === 'safari' ? (
        <SafariBanner onHow={offer.openInstructions} onDismiss={offer.dismiss} />
      ) : null}

      <InstallInstructions
        open={offer.instructionsOpen}
        onClose={offer.closeInstructions}
        onAlreadyInstalled={() => {
          offer.hideForever()
          offer.closeInstructions()
        }}
      />
    </>
  )
}

/**
 * «не во время активного набора сообщения» (§9.4 п. 2): признак — фокус
 * в многострочном поле ввода, то есть в композере C-07.
 */
function isComposing(): boolean {
  const active = document.activeElement
  return active instanceof HTMLTextAreaElement && active.value.length > 0
}

/* ===================================================================== */
/* PWA-07 — Chromium                                                      */
/* ===================================================================== */

function ChromiumBanner({
  desktop,
  onInstall,
  onDismiss,
}: {
  desktop: boolean
  onInstall: () => void
  onDismiss: () => void
}) {
  return (
    <aside
      className={cx(styles['banner'], desktop ? styles['corner'] : styles['underHeader'])}
      aria-label="Установка приложения"
    >
      <span className={styles['icon']}>
        <Icon name="download" size={20} />
      </span>

      <div className={styles['body']}>
        <div className={styles['titleBlock']}>
          <p className={styles['title']}>Установите приложение</p>
          <p className={styles['text']}>
            Отдельная иконка, запуск без адресной строки, уведомления по разрешению.
          </p>
        </div>

        <div className={styles['actions']}>
          <Button size="sm" onClick={onInstall}>
            Установить
          </Button>
          {/* На mobile «Позже» заменяет крестик — строка остаётся одной */}
          <span className={styles['later']}>
            <Button size="sm" variant="ghost" onClick={onDismiss}>
              Позже
            </Button>
          </span>
        </div>
      </div>

      <button type="button" className={styles['close']} aria-label="Скрыть" onClick={onDismiss}>
        <Icon name="close" size={16} />
      </button>
    </aside>
  )
}

/* ===================================================================== */
/* PWA-01 — мобильная Safari                                              */
/* ===================================================================== */

function SafariBanner({ onHow, onDismiss }: { onHow: () => void; onDismiss: () => void }) {
  return (
    <aside className={cx(styles['banner'], styles['aboveTabs'])} aria-label="Установка приложения">
      <span className={styles['icon']}>
        <Icon name="download" size={20} />
      </span>

      <div className={styles['body']}>
        <div className={styles['titleBlock']}>
          <p className={styles['title']}>На экран «Домой»</p>
          <p className={styles['text']}>Отдельная иконка, запуск без адресной строки, уведомления.</p>
        </div>

        <div className={styles['actions']}>
          <Button size="sm" onClick={onHow}>
            Как установить
          </Button>
          <span className={styles['later']}>
            <Button size="sm" variant="ghost" onClick={onDismiss}>
              Позже
            </Button>
          </span>
        </div>
      </div>

      <button type="button" className={styles['close']} aria-label="Скрыть" onClick={onDismiss}>
        <Icon name="close" size={16} />
      </button>
    </aside>
  )
}

/* ===================================================================== */
/* PWA-02 — инструкция                                                    */
/* ===================================================================== */

/** Шаги PWA-02; их же показывает стартовый экран во вкладке «iPhone». */
export const INSTALL_STEPS: readonly {
  title: string
  hint: string
  icon: 'share' | 'plus' | 'monitor' | 'check'
}[] = [
  { title: 'Нажмите «Поделиться»', hint: 'Кнопка внизу экрана Safari', icon: 'share' },
  { title: '«На экран „Домой“»', hint: 'Пролистайте список действий', icon: 'plus' },
  { title: '«Открыть как веб-приложение»', hint: 'Если Safari предложит выбор', icon: 'monitor' },
  { title: '«Добавить»', hint: 'Иконка появится рядом с остальными', icon: 'check' },
]

export function InstallInstructions({
  open,
  onClose,
  onAlreadyInstalled,
}: {
  open: boolean
  onClose: () => void
  onAlreadyInstalled: () => void
}): React.ReactElement {
  const standalone = isStandalone()
  const openPushPrompt = useAppStore((s) => s.openPushPrompt)

  // DoD п. 4: в установленном приложении шаги бессмысленны.
  if (standalone) {
    return (
      <Sheet
        open={open}
        onClose={onClose}
        title="Приложение уже установлено"
        description="Вы открыли «Движ» как отдельное приложение."
        footer={
          <Button
            fullWidth
            onClick={() => {
              onClose()
              openPushPrompt()
            }}
          >
            Включить уведомления
          </Button>
        }
      >
        <p className={styles['note']}>
          Осталось разрешить уведомления — иначе сообщения и совпадения будут видны только при открытом
          приложении.
        </p>
      </Sheet>
    )
  }

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title="Как добавить на экран «Домой»"
      description="Четыре шага в Safari — приложение появится рядом с остальными."
      footer={
        <>
          <Button variant="secondary" onClick={onAlreadyInstalled}>
            Уже установил(а)
          </Button>
          <Button onClick={onClose}>Понятно</Button>
        </>
      }
    >
      <ol className={styles['steps']}>
        {INSTALL_STEPS.map((step, index) => (
          <li key={step.title}>
            <span className={styles['stepNumber']}>{index + 1}</span>
            <span className={styles['stepBody']}>
              <span className={styles['stepTitle']}>
                <Icon name={step.icon} size={16} />
                {step.title}
              </span>
              <span className={styles['stepHint']}>{step.hint}</span>
            </span>
          </li>
        ))}
      </ol>

      <div className={styles['whatChanges']}>
        <p className={styles['whatTitle']}>Что изменится</p>
        <ul>
          <li>Отдельная иконка и запуск без адресной строки</li>
          <li>Уведомления — после отдельного разрешения</li>
        </ul>
      </div>

      <p className={styles['warning']}>
        В приложении понадобится войти ещё раз: сессия вкладки не переносится.
      </p>
    </Sheet>
  )
}
